import React, { useEffect, useState, useCallback } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import {
  getFirestore, doc, getDoc, setDoc,
  collection, addDoc, onSnapshot, deleteDoc, Timestamp
} from 'firebase/firestore';
import app from '../firebase';
import RegistroBomberos from './RegistroBomberos';
import CambiarClave from './CambiarClave';
import AdminTextBanners from './AdminTextBanners';
import ConfiguracionVisibilidad from './ConfiguracionVisibilidad';
import './AdminPanel.css';
import RegistroOperadoras from './RegistroOperadoras';
import GestionCompanias from './GestionCompanias';
import GestionCuerpos from './GestionCuerpos';
import GestionUnidades from './GestionUnidades';
import GestionAdministradores from './GestionAdministradores';
import GestionUsuariosPartes from './GestionUsuariosPartes';
import GestionOrganigrama from './GestionOrganigrama';
import { useCuerpo } from '../context/CuerpoContext';
import { query, where, getDocs } from 'firebase/firestore';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';

registerLocale('es', es);

const auth = getAuth(app);
const db = getFirestore(app);

function AdminPanel() {
  const { cuerpo, cuerpoId, seleccionarCuerpoPorId } = useCuerpo();
  const [cuerposDisponibles, setCuerposDisponibles] = useState([]);
  const [companias, setCompanias] = useState([]);
  const [usuarioAutenticado, setUsuarioAutenticado] = useState(false);
  const [rol, setRol] = useState(null);
  const [cargandoAuth, setCargandoAuth] = useState(true);

  const [vista, setVista] = useState('inicio');

  // Drawer responsive
  const [menuAbierto, setMenuAbierto] = useState(false);
  const abrirMenu = useCallback(() => setMenuAbierto(true), []);
  const cerrarMenu = useCallback(() => setMenuAbierto(false), []);
  const selectSection = useCallback((v) => { setVista(v); setMenuAbierto(false); }, []);

  // Citaciones state
  const [textoCitacion, setTextoCitacion] = useState('');
  const [fechaCitacion, setFechaCitacion] = useState(null);
  const [companiaCitacion, setCompaniaCitacion] = useState('Todas');
  const [citaciones, setCitaciones] = useState([]);


  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCargandoAuth(true);

      if (user) {
        setUsuarioAutenticado(true);
        try {
          const s = await getDoc(doc(db, 'administradores', user.uid));
          if (s.exists()) {
            const adminData = s.data() || {};
            const adminRol = adminData.rol || null;
            setRol(adminRol);

            // Un admin normal queda amarrado a su propio Cuerpo.
            // El superadmin puede cambiar libremente el Cuerpo activo.
            if (adminRol !== 'superadmin') {
              // Compatibilidad segura con administradores históricos de Huasco
              // que fueron creados antes de incorporar cuerpo_id.
              const cuerpoAdmin = adminData.cuerpo_id || 'Huasco';
              await seleccionarCuerpoPorId(cuerpoAdmin, false);
            }
          } else {
            setRol(null);
          }
        } catch {
          setRol(null);
        } finally {
          setCargandoAuth(false);
        }
      } else {
        setUsuarioAutenticado(false);
        setRol(null);
        setCargandoAuth(false);
      }
    });

    return () => unsub();
  }, [seleccionarCuerpoPorId]);

  // El superadmin puede cambiar de Cuerpo sin cerrar sesión.
  useEffect(() => {
    if (rol !== 'superadmin') {
      setCuerposDisponibles([]);
      return;
    }

    const unsub = onSnapshot(collection(db, 'cuerpos_bomberos'), (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      arr.sort((a, b) => Number(a.orden || 9999) - Number(b.orden || 9999) || String(a.nombre || '').localeCompare(String(b.nombre || '')));
      setCuerposDisponibles(arr);
    });

    return () => unsub();
  }, [rol]);

  // ✅ Solo admins/superadmins pueden ver el panel
  const permitido = rol === 'admin' || rol === 'superadmin';

  // Suscripción a citaciones (solo si está permitido)
  useEffect(() => {
    if (!usuarioAutenticado || !permitido) return;

    if (!cuerpoId) return;
    const ref = query(collection(db, 'citaciones'), where('cuerpo_id', '==', cuerpoId));
    const unsub = onSnapshot(ref, (snap) => {
      const arr = [];
      snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
      arr.sort((a, b) => {
        const da = a.fecha?.toDate ? a.fecha.toDate().getTime() : 0;
        const dbt = b.fecha?.toDate ? b.fecha.toDate().getTime() : 0;
        return dbt - da;
      });
      setCitaciones(arr);
    });

    return () => unsub();
  }, [usuarioAutenticado, permitido, cuerpoId]);

  useEffect(() => {
    if (!usuarioAutenticado || !permitido || !cuerpoId) return;
    (async () => {
      const snap = await getDocs(query(collection(db, "companias"), where("cuerpo_id", "==", cuerpoId)));
      const arr = snap.docs.map(d => ({ id:d.id, ...d.data() })).sort((a,b)=>Number(a.orden||999)-Number(b.orden||999));
      setCompanias(arr);
    })().catch(console.error);
  }, [usuarioAutenticado, permitido, cuerpoId]);

  const cerrarSesion = async () => {
    await signOut(auth);
  };

  const crearCitacion = async () => {
    if (!textoCitacion || !fechaCitacion) {
      alert('Debes ingresar texto y fecha.');
      return;
    }
    try {
      const ref = collection(db, 'citaciones');
      await addDoc(ref, {
        texto: textoCitacion,
        fecha: Timestamp.fromDate(fechaCitacion),
        compania: companiaCitacion || 'Todas',
        createdAt: Timestamp.now(),
        cuerpo_id: cuerpoId,
      });
      setTextoCitacion('');
      setFechaCitacion(null);
      setCompaniaCitacion('Todas');
      alert('Citacion creada correctamente.');
    } catch (err) {
      console.error(err);
      alert('Error al crear citación.');
    }
  };

  const eliminarCitacion = async (id) => {
    if (!window.confirm('¿Eliminar esta citación?')) return;
    try {
      await deleteDoc(doc(db, 'citaciones', id));
    } catch (err) {
      console.error(err);
      alert('Error al eliminar citación.');
    }
  };


  // -------------------------
  // Render de estados
  // -------------------------
  if (cargandoAuth) {
    return (
      <div className="admin-main" style={{ padding: 24 }}>
        <h2>Cargando...</h2>
      </div>
    );
  }

  if (!usuarioAutenticado) {
    return (
      <div className="admin-main" style={{ padding: 24 }}>
        <h2>Debes iniciar sesión para ver el panel.</h2>
      </div>
    );
  }

  // ✅ Bloqueo REAL si no es admin/superadmin
  if (!permitido) {
    return (
      <div className="admin-main" style={{ padding: 24 }}>
        <h2>Acceso restringido</h2>
        <p>No tienes permisos para acceder al panel de administración.</p>
        <button onClick={cerrarSesion}>Cerrar sesión</button>
      </div>
    );
  }

  return (
    <>
      {/* Toggle y backdrop (solo móvil por CSS) */}
      <button className="admin-menu-toggle" onClick={abrirMenu} aria-label="Abrir menú">
        ☰ Menú
      </button>
      <div className={`admin-backdrop ${menuAbierto ? 'is-open' : ''}`} onClick={cerrarMenu} />

      <div className="admin-container">
        <aside className={`admin-menu drawer ${menuAbierto ? 'is-open' : ''}`}>
          <h3>Panel ({rol})</h3>

          {rol === 'superadmin' && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', color: 'white', fontSize: 13, marginBottom: 5 }}>
                Cuerpo actual
              </label>
              <select
                value={cuerpoId || ''}
                onChange={(e) => seleccionarCuerpoPorId(e.target.value)}
                style={{ width: '100%', padding: 8, borderRadius: 6 }}
              >
                {cuerposDisponibles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre || c.id}{c.activo === false ? ' (Inactivo)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button onClick={() => selectSection('inicio')}>Inicio</button>
          {rol === 'superadmin' && (
            <button onClick={() => selectSection('cuerpos')}>Cuerpos de Bomberos</button>
          )}
          <button onClick={() => selectSection('registro')}>Registro Bomberos</button>
          <button onClick={() => selectSection('registroOperadoras')}>Registro Operadoras</button>
          <button onClick={() => selectSection('companias')}>Compañías</button>
          <button onClick={() => selectSection('unidades')}>Unidades</button>
          <button onClick={() => selectSection('citaciones')}>Citaciones</button>
          <button onClick={() => selectSection('bannersTexto')}>Banners de texto</button>
          <button onClick={() => selectSection('visibilidad')}>Visibilidad de páginas</button>
          {(rol === 'admin' || rol === 'superadmin') && (
            <button onClick={() => selectSection('organigrama')}>Organigrama</button>
          )}
          <button onClick={() => selectSection('clave')}>Cambiar Clave</button>
          {rol === 'superadmin' && (
            <button onClick={() => selectSection('administradores')}>Administradores</button>
          )}
          <button onClick={() => selectSection('usuariosPartes')}>Usuarios de Partes</button>
          <button onClick={cerrarSesion}>Cerrar sesión</button>
        </aside>

        <main className="admin-main">
          {vista === 'inicio' && (
            <div>
              <h2>Bienvenido al Panel de Administración</h2>
              <p><strong>Cuerpo actual:</strong> {cuerpo?.nombre || cuerpoId || 'Sin seleccionar'}</p>
              <p>Selecciona una opción en el menú.</p>
            </div>
          )}

          {vista === 'cuerpos' && rol === 'superadmin' && <GestionCuerpos />}
          {vista === 'registro' && <RegistroBomberos />}
          {vista === 'registroOperadoras' && <RegistroOperadoras />}
          {vista === 'companias' && <GestionCompanias />}
          {vista === 'unidades' && <GestionUnidades />}
          {vista === 'bannersTexto' && <AdminTextBanners />}
          {vista === 'visibilidad' && <ConfiguracionVisibilidad />}
          {vista === 'organigrama' && (rol === 'admin' || rol === 'superadmin') && <GestionOrganigrama />}
          {vista === 'clave' && <CambiarClave />}

          {vista === 'citaciones' && (
            <div>
              <h2>Citaciones</h2>
              <div className="formulario" style={{ maxWidth: 720 }}>
                <div>
                  <label>Texto</label>
                  <textarea
                    value={textoCitacion}
                    onChange={(e) => setTextoCitacion(e.target.value)}
                  />
                </div>
                <div>
                  <label>Fecha y hora</label>
                  <ReactDatePicker
                    selected={fechaCitacion}
                    onChange={(date) => setFechaCitacion(date)}
                    showTimeSelect
                    timeIntervals={5}
                    timeCaption="Hora"
                    dateFormat="Pp"
                    locale="es"
                    placeholderText="Seleccionar fecha y hora"
                  />
                </div>
                <div>
                  <label>Compañía</label>
                  <select
                    value={companiaCitacion}
                    onChange={(e) => setCompaniaCitacion(e.target.value)}
                  >
                    <option value="Todas">Todas</option>
                    {companias.map((c) => (
                      <option key={c.id} value={c.nombre}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
                <button onClick={crearCitacion}>Guardar citación</button>
              </div>

              <h3 style={{ marginTop: 24 }}>Citaciones creadas</h3>
              <table className="tabla-citaciones">
                <thead>
                  <tr>
                    <th>Texto</th>
                    <th>Fecha</th>
                    <th>Compañía</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {citaciones.map((c) => (
                    <tr key={c.id}>
                      <td style={{ whiteSpace: 'normal' }}>{c.texto}</td>
                      <td>
                        {c.fecha?.toDate
                          ? c.fecha.toDate().toLocaleString()
                          : c.fecha || ''}
                      </td>
                      <td>{c.compania || 'Todas'}</td>
                      <td>
                        <button onClick={() => eliminarCitacion(c.id)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                  {citaciones.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center' }}>
                        No hay citaciones registradas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {vista === 'administradores' && rol === 'superadmin' && (
            <GestionAdministradores cuerpoActualId={cuerpoId} />
          )}

          {vista === 'usuariosPartes' && (rol === 'admin' || rol === 'superadmin') && (
            <GestionUsuariosPartes cuerpoActualId={cuerpoId} rolActor={rol} />
          )}
        </main>
      </div>
    </>
  );
}

export default AdminPanel;
